import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Sparkles, Loader2, Send, Database, Inbox, RefreshCw, CheckCheck } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AlertaCard from '../../src/components/Vick/AlertaCard';
import RecadosVick from '../../src/components/Vick/RecadosVick';
import { useVickChat } from '../../src/hooks/useVickChat';
import {
  useIaAlertas,
  FILTROS_PADRAO,
  ROTULO_MODULO,
  ROTULO_SEVERIDADE,
  type FiltrosAlertas,
} from '../../src/hooks/useIaAlertas';
import { useAlertasLidos, alertaNaoLido } from '../../src/hooks/useAlertasLidos';
import { useAuth } from '../../src/contexts/AuthContext';
import { MODULOS_ALERTA, modulosDeAlertaVisiveis } from '../../src/lib/vickAlertas';
import type { IaAlerta, IaAlertaModulo, IaAlertaSeveridade } from '../../types';

/**
 * A Vick em tela cheia: a fila de alertas à esquerda, a conversa à direita.
 *
 * Por que duas colunas e não duas telas: a pergunta que segue um alerta é quase
 * sempre "por que isso aconteceu?". Ter que sair da fila para perguntar, e
 * depois voltar para achar de novo onde estava, é o tipo de atrito que faz a
 * triagem não acontecer.
 *
 * O painel flutuante (VickChatPanel) continua existindo para a pergunta rápida
 * no meio de outra tela. Aqui é a sessão de gestão: filtro, histórico, triagem.
 */

const markdownComponents = {
  h1: ({ children }: any) => <h1 className="text-base font-bold text-gray-800 dark:text-white border-b border-gray-200 dark:border-slate-700 pb-1 mb-2 mt-3 first:mt-0">{children}</h1>,
  h2: ({ children }: any) => <h2 className="text-sm font-bold text-gray-800 dark:text-white mb-2 mt-3 first:mt-0">{children}</h2>,
  h3: ({ children }: any) => <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-1 mt-2 first:mt-0">{children}</h3>,
  p: ({ children }: any) => <p className="text-gray-700 dark:text-slate-300 leading-relaxed mb-2 last:mb-0 text-sm">{children}</p>,
  strong: ({ children }: any) => <strong className="font-semibold text-gray-900 dark:text-white">{children}</strong>,
  ul: ({ children }: any) => <ul className="list-disc list-inside space-y-1 mb-2 text-gray-700 dark:text-slate-300 text-sm">{children}</ul>,
  ol: ({ children }: any) => <ol className="list-decimal list-inside space-y-1 mb-2 text-gray-700 dark:text-slate-300 text-sm">{children}</ol>,
  li: ({ children }: any) => <li className="text-gray-700 dark:text-slate-300">{children}</li>,
  table: ({ children }: any) => (
    <div className="overflow-x-auto my-2">
      <table className="min-w-full text-xs border border-gray-200 dark:border-slate-700 rounded-lg">{children}</table>
    </div>
  ),
  th: ({ children }: any) => <th className="text-left px-2 py-1 bg-gray-50 dark:bg-slate-800 font-semibold text-gray-700 dark:text-slate-200">{children}</th>,
  td: ({ children }: any) => <td className="px-2 py-1 border-t border-gray-100 dark:border-slate-700 text-gray-700 dark:text-slate-300">{children}</td>,
  code: ({ children, ...props }: any) => (
    <code className="bg-gray-100 dark:bg-slate-700 text-primary px-1 py-0.5 rounded text-xs font-mono" {...props}>{children}</code>
  ),
};

const SEVERIDADES: (IaAlertaSeveridade | 'todas')[] = ['todas', 'alta', 'media', 'baixa'];

const Pilula: React.FC<{ ativo: boolean; onClick: () => void; children: React.ReactNode }> = ({ ativo, onClick, children }) => (
  <button
    onClick={onClick}
    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
      ativo
        ? 'bg-primary text-white border-primary'
        : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-slate-300 border-gray-200 dark:border-slate-700 hover:border-primary/40 dark:hover:border-primary-light/50'
    }`}
  >
    {children}
  </button>
);

const VickView: React.FC = () => {
  // A notificação do vigia aponta para /vick?modulo=<modulo>. Sem ler o
  // parâmetro, o recado sobre o Financeiro caía numa fila "Todos os módulos"
  // ordenada por gravidade — com o alerta citado enterrado abaixo dos urgentes
  // de outro time. Era o "me jogou pra lugar nenhum": a rota existia, o
  // destino não.
  const [searchParams] = useSearchParams();
  const moduloDaUrl = searchParams.get('modulo');
  // `?notificacao=<id>` diz QUAL recado trouxe a pessoa até aqui. Sem isso, o
  // clique em "Resumo do dia" abria a fila de alertas e o texto do resumo —
  // que é o que a pessoa foi ler — não aparecia em canto nenhum.
  const recadoDaUrl = searchParams.get('notificacao');
  const [filtros, setFiltros] = useState<FiltrosAlertas>(FILTROS_PADRAO);
  const { alertas, loading, erro, recarregar, baixar, temAcesso } = useIaAlertas(filtros);
  const { profile } = useAuth();
  const { lidosAte, marcarLidos } = useAlertasLidos();
  // "Não lido" só faz sentido no que está em aberto. Nas abas Resolvidos e
  // Ignorados o trabalho já acabou — marcar leitura ali não significaria nada.
  const naoLidos = filtros.estado === 'aberto'
    ? alertas.filter((a) => alertaNaoLido(a.detectado_em, lidosAte)).length
    : 0;

  // Clicar de novo na notificação (já estando em /vick) tem que MEXER na tela.
  // Reagir em render, e não em efeito, evita o quadro de um instante com o
  // filtro antigo.
  const [moduloAplicado, setModuloAplicado] = useState<string | null>(moduloDaUrl);
  if (moduloDaUrl !== moduloAplicado) {
    setModuloAplicado(moduloDaUrl);
    // Módulo desconhecido na URL cai em "todos": pílula selecionada que não
    // existe na lista some da tela e o filtro fica invisível, parecendo bug.
    const valido = MODULOS_ALERTA.includes(moduloDaUrl as IaAlertaModulo);
    setFiltros((f) => ({
      ...f,
      estado: 'aberto',
      modulo: valido ? (moduloDaUrl as IaAlertaModulo) : 'todos',
    }));
  }
  // Só os módulos que esta pessoa alcança. Pílula de módulo que sempre volta
  // vazia parece filtro quebrado, não permissão ausente.
  const modulos = useMemo<(IaAlertaModulo | 'todos')[]>(
    () => ['todos', ...modulosDeAlertaVisiveis(profile)],
    [profile],
  );
  const {
    messages, sending, error: erroChat, modo, setModo, sendMessage, resetConversation,
  } = useVickChat();
  const [input, setInput] = useState('');

  const porSeveridade = useMemo(() => {
    const c: Record<string, number> = { alta: 0, media: 0, baixa: 0 };
    for (const a of alertas) c[a.severidade] += 1;
    return c;
  }, [alertas]);

  const enviar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    sendMessage(input);
    setInput('');
  };

  /**
   * Leva o alerta para a conversa já com os números dentro.
   *
   * O contexto vai no texto da pergunta de propósito: a Vick não tem acesso à
   * fila de alertas pelas consultas dela, e mandar só "por que isso aconteceu?"
   * faria ela responder no vazio.
   */
  const perguntarSobre = (a: IaAlerta) => {
    const pergunta =
      `Sobre este alerta: "${a.titulo}" (módulo ${ROTULO_MODULO[a.modulo]}, ` +
      `detectado em ${new Date(a.detectado_em).toLocaleString('pt-BR')}).\n` +
      `Dados do alerta: ${JSON.stringify(a.dados)}\n\n` +
      `Explique o que provavelmente causou isso e o que conferir primeiro.`;
    sendMessage(pergunta);
  };

  return (
    <div className="p-4 md:p-6 h-[calc(100vh-4rem)] flex flex-col">
      <header className="flex items-center gap-3 mb-4 shrink-0">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 text-white flex items-center justify-center shrink-0">
          <Sparkles size={20} />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">Vick</h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 leading-tight">
            Acompanha a operação de hora em hora e responde com os dados do sistema.
          </p>
        </div>
      </header>

      {/* Sem nenhum módulo liberado a coluna de alertas some e a conversa ocupa
          a tela toda — em vez de uma fila permanentemente vazia. O que libera é
          a permissão "Vick — acesso a dados" em Config > Permissões Especiais. */}
      <div className={`flex-1 min-h-0 grid grid-cols-1 gap-4 ${temAcesso ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]' : ''}`}>
        {/* ───────── Fila de alertas ───────── */}
        {temAcesso && (
        <section className="flex flex-col min-h-0 bg-gray-50 dark:bg-slate-900/50 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          <RecadosVick destacado={recadoDaUrl} />

          <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 shrink-0 bg-white dark:bg-slate-800/50">
            <div className="flex items-center justify-between gap-2 mb-2.5">
              <h2 className="text-sm font-bold text-gray-800 dark:text-white">
                {filtros.estado === 'aberto' ? 'Em aberto' : filtros.estado === 'resolvido' ? 'Resolvidos' : 'Ignorados'}
                <span className="ml-1.5 text-gray-400 font-normal">{alertas.length}</span>
              </h2>
              <div className="flex items-center gap-1 shrink-0">
                {/* Ler não é resolver: marca só o contador desta pessoa. */}
                {naoLidos > 0 ? (
                  <button
                    onClick={marcarLidos}
                    title="Marcar os alertas atuais como lidos (não resolve nada)"
                    className="inline-flex items-center gap-1 text-[11px] text-primary dark:text-primary-light hover:underline font-medium px-1"
                  >
                    <CheckCheck size={13} /> Marcar como lidos
                  </button>
                ) : null}
                <button
                  onClick={recarregar}
                  title="Atualizar"
                  className="p-1.5 text-gray-400 hover:text-primary rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>

            {filtros.estado === 'aberto' && alertas.length > 0 && (
              <p className="text-[11px] text-gray-500 dark:text-slate-400 mb-2.5">
                {porSeveridade.alta} urgente(s) · {porSeveridade.media} de atenção · {porSeveridade.baixa} informativo(s)
              </p>
            )}

            <div className="flex flex-wrap gap-1.5">
              {(['aberto', 'resolvido', 'ignorado'] as const).map((e) => (
                <Pilula key={e} ativo={filtros.estado === e} onClick={() => setFiltros((f) => ({ ...f, estado: e }))}>
                  {e === 'aberto' ? 'Em aberto' : e === 'resolvido' ? 'Resolvidos' : 'Ignorados'}
                </Pilula>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {modulos.map((m) => (
                <Pilula key={m} ativo={filtros.modulo === m} onClick={() => setFiltros((f) => ({ ...f, modulo: m }))}>
                  {m === 'todos' ? 'Todos os módulos' : ROTULO_MODULO[m]}
                </Pilula>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {SEVERIDADES.map((s) => (
                <Pilula key={s} ativo={filtros.severidade === s} onClick={() => setFiltros((f) => ({ ...f, severidade: s }))}>
                  {s === 'todas' ? 'Qualquer gravidade' : ROTULO_SEVERIDADE[s]}
                </Pilula>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {erro && (
              <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{erro}</div>
            )}
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 size={22} className="animate-spin text-gray-400" /></div>
            ) : alertas.length === 0 ? (
              <div className="text-center text-gray-400 dark:text-slate-600 mt-12 px-6">
                <Inbox size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {filtros.estado === 'aberto'
                    ? 'Nada fora do lugar com esse filtro. Eu verifico de hora em hora.'
                    : 'Nenhum alerta nesse estado.'}
                </p>
              </div>
            ) : (
              alertas.map((a) => (
                <AlertaCard
                  key={a.id}
                  alerta={a}
                  naoLido={filtros.estado === 'aberto' && alertaNaoLido(a.detectado_em, lidosAte)}
                  onBaixar={filtros.estado === 'aberto' ? baixar : undefined}
                  onPerguntar={perguntarSobre}
                />
              ))
            )}
          </div>
        </section>
        )}

        {/* ───────── Conversa ───────── */}
        <section className="flex flex-col min-h-0 bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 shrink-0 flex items-center justify-between">
            <h2 className="text-sm font-bold text-gray-800 dark:text-white">Conversa</h2>
            {messages.length > 0 && (
              <button
                onClick={resetConversation}
                className="text-xs text-gray-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light transition-colors"
              >
                Nova conversa
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-center text-gray-400 dark:text-slate-600 text-sm mt-12 px-6">
                <Sparkles size={28} className="mx-auto mb-2 opacity-50" />
                <p className="mb-3">Pergunte sobre a operação e eu consulto os dados na hora.</p>
                <ul className="text-xs space-y-1 text-left inline-block">
                  <li>· "Como está a carteira deste mês?"</li>
                  <li>· "Quanto vou receber em outubro?"</li>
                  <li>· "Quais UCs estão sem saldo de energia?"</li>
                  <li>· "Alguma usina parada agora?"</li>
                </ul>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 ${
                    m.role === 'user'
                      ? 'bg-primary text-white rounded-br-sm'
                      : 'bg-gray-100 dark:bg-slate-900 rounded-bl-sm'
                  }`}
                >
                  {m.role === 'assistant' ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{m.content}</ReactMarkdown>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                  )}
                  {/* Procedência: qual consulta sustenta o número. Sem isto não
                      dá para conferir, e número sem conferência vira boato. */}
                  {m.sources && m.sources.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-slate-700 flex flex-wrap gap-1.5">
                      {m.sources.map((s, si) => (
                        <span
                          key={`${s.consulta ?? s.documentId}-${si}`}
                          className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-slate-400 bg-white dark:bg-slate-800 px-2 py-0.5 rounded-full border border-gray-200 dark:border-slate-700"
                        >
                          <Database size={11} /> {s.titulo}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-slate-900 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-gray-400 dark:text-slate-500">
                  Consultando o sistema...
                </div>
              </div>
            )}
            {erroChat && (
              <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{erroChat}</div>
            )}
          </div>

          <form onSubmit={enviar} className="border-t border-gray-200 dark:border-slate-700 p-3 shrink-0">
            <label className="flex items-center gap-1.5 mb-2 text-[11px] text-gray-500 dark:text-slate-400 cursor-pointer w-fit">
              <input
                type="checkbox"
                checked={modo === 'planejamento'}
                onChange={(e) => setModo(e.target.checked ? 'planejamento' : 'resposta')}
                className="rounded border-gray-300 dark:border-slate-600 text-primary focus:ring-primary"
              />
              Modo planejamento (resposta longa e estruturada)
            </label>
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    enviar(e);
                  }
                }}
                placeholder="Pergunte algo para a Vick..."
                rows={1}
                className="flex-1 resize-none px-3 py-2.5 text-sm bg-gray-100 dark:bg-slate-900 border-0 rounded-xl focus:ring-2 focus:ring-primary outline-none dark:text-white max-h-32"
              />
              <button
                type="submit"
                disabled={!input.trim() || sending}
                className="p-2.5 bg-primary hover:bg-primary/90 disabled:opacity-40 text-white rounded-xl transition-colors shrink-0"
              >
                <Send size={16} />
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default VickView;
